import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { useRef } from 'react';
import { Lightbulb, Layers, Store, Fuel, Building2, Megaphone, ArrowUpRight } from 'lucide-react';

const services = [
  {
    icon: Lightbulb,
    title: 'LED Sign Boards',
    description: 'Premium glow sign boards in Ahmedabad with energy-efficient LED technology. Custom LED signage solutions for retail, commercial & industrial applications.',
    keywords: ['Glow Sign Board Ahmedabad', 'LED signage Gujarat'],
    image: 'https://images.unsplash.com/photo-1769710329350-167616dd56cf?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxNzV8MHwxfHNlYXJjaHw0fHxpbGx1bWluYXRlZCUyMHN0b3JlJTIwc2lnbiUyMG5pZ2h0fGVufDB8fHx8MTc3MzkwNjY3M3ww&ixlib=rb-4.1.0&q=85',
  },
  {
    icon: Layers,
    title: 'Acrylic Signage & 3D Letters',
    description: 'High-quality acrylic signage in Ahmedabad with precision-cut 3D letters. Modern, durable & visually stunning signage for brand elevation.',
    keywords: ['Acrylic signage Ahmedabad', '3D signage Gujarat'],
    image: 'https://images.unsplash.com/photo-1767460775045-a0bf02c997af?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2Mzl8MHwxfHNlYXJjaHwyfHxuZW9uJTIwc2lnbiUyMGN1c3RvbSUyMGFydGlzdGljfGVufDB8fHx8MTc3MzkwNjY2Mnww&ixlib=rb-4.1.0&q=85',
  },
  {
    icon: Store,
    title: 'Retail Shop Signage',
    description: 'Complete retail signage solutions in Ahmedabad. Transform your storefront with eye-catching displays that drive foot traffic.',
    keywords: ['Retail signage Ahmedabad', 'Shop board design'],
    image: 'https://images.unsplash.com/photo-1771219189428-49fa966884be?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2Mzl8MHwxfHNlYXJjaHw0fHxtb2Rlcm4lMjByZXRhaWwlMjBzdG9yZWZyb250JTIwc2lnbmFnZSUyMG5pZ2h0fGVufDB8fHx8MTc3MzkwNjY2NXww&ixlib=rb-4.1.0&q=85',
  },
  {
    icon: Fuel,
    title: 'Petrol Pump Signage',
    description: 'Specialized petrol pump signage with weather-resistant materials. Complete branding solutions for fuel stations across Gujarat.',
    keywords: ['Petrol pump signage India', 'Fuel station branding'],
    image: 'https://images.unsplash.com/photo-1744664429918-5d26ae83f883?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzd8MHwxfHNlYXJjaHw0fHxzaG9wcGluZyUyMG1hbGwlMjB0b3RlbSUyMHNpZ25hZ2UlMjBweWxvbnxlbnwwfHx8fDE3NzM5MDY2NzR8MA&ixlib=rb-4.1.0&q=85',
  },
  {
    icon: Building2,
    title: 'Indoor & Outdoor Signage',
    description: 'Comprehensive outdoor signage solutions. From building facades to wayfinding systems, we deliver impactful visual communication.',
    keywords: ['Outdoor signage Ahmedabad', 'Indoor signage Gujarat'],
    image: 'https://images.unsplash.com/photo-1771947674131-ad81d3f85da6?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODF8MHwxfHNlYXJjaHwyfHxtb2Rlcm4lMjBlbWVyZ2VuY3klMjBleGl0JTIwc2lnbnxlbnwwfHx8fDE3NzM5MDY2NzR8MA&ixlib=rb-4.1.0&q=85',
  },
  {
    icon: Building2,
    title: 'Corporate Office Branding',
    description: 'Professional corporate signage for reception areas, conference rooms & office spaces. Elevate your brand presence.',
    keywords: ['Corporate signage Gujarat', 'Office branding'],
    image: 'https://images.unsplash.com/photo-1758448656987-cfae6bf225e4?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODB8MHwxfHNlYXJjaHwxfHxsdXh1cnklMjBvZmZpY2UlMjBsb2JieSUyMGJyYW5kaW5nJTIwbG9nbyUyMHdhbGx8ZW58MHx8fHwxNzczOTA2NjY0fDA&ixlib=rb-4.1.0&q=85',
  },
  {
    icon: Megaphone,
    title: 'Hoardings & Displays',
    description: 'Large format hoardings and advertising displays for maximum visibility. Strategic outdoor advertising across Ahmedabad.',
    keywords: ['Hoardings Ahmedabad', 'Advertising displays'],
    image: 'https://images.unsplash.com/photo-1768221681355-37fb3af8a17a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTN8MHwxfHNlYXJjaHwxfHxkaWdpdGFsJTIwdmlkZW8lMjB3YWxsJTIwZGlzcGxheSUyMGNvcnBvcmF0ZXxlbnwwfHx8fDE3NzM5MDY2NjN8MA&ixlib=rb-4.1.0&q=85',
  },
];

const ServiceCard = ({ service, index }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });
  const Icon = service.icon;

  return (
    <motion.article
      ref={ref}
      className="service-card relative group cursor-pointer overflow-hidden"
      initial={{ opacity: 0, y: 40 }}
      animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 40 }}
      transition={{ duration: 0.6, delay: index * 0.1 }}
      data-testid={`service-card-${index}`}
    >
      <div className="relative z-10">
        {/* Icon */}
        <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-sky-100 to-violet-100 flex items-center justify-center mb-5 group-hover:scale-110 transition-transform duration-300">
          <Icon className="w-7 h-7 text-sky-500" />
        </div>

        {/* Title */}
        <h3 className="text-xl font-['Oswald'] font-bold text-slate-800 mb-3 group-hover:text-sky-500 transition-colors duration-300">
          {service.title}
        </h3>

        {/* Description */}
        <p className="text-sm text-slate-500 font-['Manrope'] leading-relaxed mb-4">
          {service.description}
        </p>

        {/* Keywords */}
        <div className="flex flex-wrap gap-2">
          {service.keywords.map((keyword, i) => (
            <span
              key={i}
              className="text-xs px-3 py-1 bg-slate-50 border border-slate-200 rounded-full text-slate-500"
            >
              {keyword}
            </span>
          ))}
        </div>

        {/* Arrow */}
        <div className="flex items-center gap-2 text-sky-500 mt-4 opacity-0 group-hover:opacity-100 transition-all duration-300 transform translate-y-2 group-hover:translate-y-0">
          <span className="text-sm font-medium">Learn More</span>
          <ArrowUpRight className="w-4 h-4" />
        </div>
      </div>
    </motion.article>
  );
};

const ServicesSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });

  return (
    <section
      id="services"
      className="py-24 md:py-32 bg-slate-50 relative overflow-hidden"
      data-testid="services-section"
    >
      <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12 relative z-10">
        {/* Section Header */}
        <motion.div
          ref={ref}
          className="text-center mb-16 md:mb-20"
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
          transition={{ duration: 0.6 }}
        >
          <span className="text-sky-500 text-sm font-semibold uppercase tracking-[0.2em] mb-4 block font-['Manrope']">
            Our Services
          </span>
          <h2 className="section-heading mb-6">
            PREMIUM SIGNAGE <span className="gradient-text">SOLUTIONS</span>
          </h2>
          <p className="section-subheading mx-auto">
            From concept to installation, we deliver comprehensive signage solutions 
            that transform brands and captivate audiences across Gujarat.
          </p>
        </motion.div>

        {/* Services Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {services.map((service, index) => (
            <ServiceCard key={index} service={service} index={index} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default ServicesSection;
